import cn from 'classnames'
import CustomImage from 'shared/components/CustomImage'
import { MouseEventHandler, memo, useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { toggleSelectingItem } from 'utils/common'
import PreviewText from 'views/find-art/components/Quiz/PreviewText'
import classNames from 'classnames'
import { FIND_ART_STYLES_TYPES } from 'views/find-art/config'
import { StyleType } from 'views/find-art/types'

const StyleStep = ({ onNext }: { onNext: () => void }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') onNext()
    }

    // Attach the event listener when the component mounts
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 text-kokushoku-black lg:gap-12">
      {/* preview */}
      <PreviewText displayProps={['roomType']} />

      <div className="text-center text-[25px] font-semibold leading-[1.25] lg:text-[36px]">
        What style of art are you looking for?
      </div>
      <p className="text-center text-[14px] leading-[1.25] text-kokushoku-black lg:text-[18px]">
        Select as many as you like
      </p>
      <div
        className={classNames(
          'container h-full w-full md:gap-4 lg:grid-cols-5 xl:grid-cols-6',
          'grid w-full grid-cols-3 gap-2 md:grid-cols-4 lg:gap-3 ',
        )}
      >
        {FIND_ART_STYLES_TYPES.map((styleType) => (
          <StyleItem key={styleType.value} styleType={styleType} />
        ))}
      </div>
    </div>
  )
}

export default StyleStep

const StyleItem = memo(
  ({ styleType }: { styleType: StyleType }) => {
    const { label, value, ratio, image, imageSmall } = styleType

    const { watch, setValue, trigger, getValues } = useFormContext<{
      artStyles: string[]
      businessType?: string
      roomType?: string
      numOfRoomsMin?: number
      numOfRoomsMax?: number
    }>()

    const selectedArtStyles = watch('artStyles') || []

    const makeHandleToggleArtStyle = useCallback(
      (styleType: StyleType): MouseEventHandler<HTMLDivElement> =>
        (e) => {
          e.preventDefault()
          const selectedArtStyles = getValues('artStyles') || []
          const nextSelectedArtStyles = toggleSelectingItem(selectedArtStyles, styleType.value)
          setValue('artStyles', nextSelectedArtStyles) // clear the value if selecting other, so the input is empty
          trigger('artStyles')
        },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    )

    const isSelected = selectedArtStyles.includes(value)

    return (
      <div
        key={styleType.value}
        className="flex flex-col gap-2"
        onClick={makeHandleToggleArtStyle(styleType)}
        role="button"
      >
        <div
          className={cn(
            'relative w-full overflow-hidden rounded-xl  transition-all lg:rounded-2xl',
            isSelected ? 'border-[3px] border-primary' : 'border border-black/10',
            'hover:border-primary hover:shadow',
          )}
          style={{ aspectRatio: ratio }}
        >
          <CustomImage
            src={image}
            placeholder="blur"
            blurDataURL={imageSmall}
            alt={label}
            fill
            className="object-cover object-center"
            priority
          />
        </div>
        <div
          className={cn(
            'pb-2 text-center text-xs font-medium leading-[1.25] lg:text-[22px]',
            isSelected ? 'text-primary' : 'text-black',
          )}
        >
          {label}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => prevProps.styleType.value === nextProps.styleType.value,
)

StyleItem.displayName = 'StyleItem'
